// addMaterial.tsx
import { useState, useRef } from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    Divider,
    FormControl,
    FormLabel,
    TextField,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
} from '@mui/material';

import { useCommonStyles } from '../../styles/GenericStyle';
import ScanInput from '../ScanInput';
import ScanButton from '../spLot/ScanIcon';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAddMaterial, useCheckMatidExistMutation, useGetSPLotWorkflowDetails} from '../../api/vegApl';
// import {
//     getMaterialMandatoryMapByCrop,
//     // getMaterialDefaultValuesByCrop,
//     // ALL_MATERIAL_FIELDS_MANDATORY,
//     // type MaterialMandatoryMap,
// } from '../../utils/materialMandatoryFields';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { DASH_PLACEHOLDER } from '../../utils/missingValue';
import { useGlobalState } from '../../contexts/GlobalState';
import { useConfirm } from '../../contexts/confirmDialogContext';
import { GenericConstant } from '../../utils/GenericConstant';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import  {numericOnlyKeyDown} from '../../utils/HelperFunction';
import FullScreenActionLoader from '../common/FullScreenActionLoader';

export type AddMaterialProps = {
    spLotId: string;
    onBack: () => void;
    onSaveToHome: () => void;
};

export default function AddMaterial({ spLotId, onBack, onSaveToHome }: AddMaterialProps) {
    const classes = useCommonStyles();
    const { showSnackbar } = useSnackbar();
    const { confirm } = useConfirm();
    const { remarks, priorities: priorityArr, state: globalState } = useGlobalState();
    const [scannedValue, setScannedValue] = useState('');
    const [isMaterialValidating, setIsMaterialValidating] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [validatedMaterialId, setValidatedMaterialId] = useState('');
    const [isMaterialFormView, setIsMaterialFormView] = useState(false);
    const [weight, setWeight] = useState('');
    const [fruitCount, setFruitCount] = useState('');
    const [bags, setBags] = useState('1');
    const [commentId, setCommentId] = useState('');
    const [priority, setPriority] = useState('');
    const [isAddMultipleMaterial, setIsAddMultipleMaterial] = useState(true);
    const [siteCode, setSiteCode] = useState('');
    const [cropCode, setCropCode] = useState('');
    const [pendingMatId, setPendingMatId] = useState('');
    const confirmThresholdRef = useRef(false);
    // const [mandatoryFields, setMandatoryFields] = useState<MaterialMandatoryMap>(ALL_MATERIAL_FIELDS_MANDATORY);
    const [isApplyingFieldRules, setIsApplyingFieldRules] = useState(false);
    const cropCodeRef = useRef('');
    const materialIdInputRef = useRef<HTMLInputElement>(null);

    // const { refetch: refetchCrops } = useGetRefCrops(siteCode, {
    //     query: {
    //         enabled: false,
    //         refetchOnReconnect: false,
    //         refetchOnWindowFocus: false,
    //         refetchOnMount: false,
    //     },
    // });

    const { mutate: getSPLotWorkflowDetails } = useGetSPLotWorkflowDetails({
        mutation: {
            onSuccess: (response: any) => {
                const data = response?.data?.[0];
                const resolvedCropCode = data?.Crop_Code || '';
                const resolvedSiteCode = data?.Site_Code || '';
                setSiteCode(resolvedSiteCode);
                setCropCode(resolvedCropCode);
                cropCodeRef.current = resolvedCropCode;

                if (!pendingMatId) {
                    setIsMaterialValidating(false);
                    showSnackbar({ message: 'Material ID is required', severity: 'warning' });
                    return;
                }

                checkMatidExists({ data: { matid: pendingMatId, splot_id: spLotId, crop_code: resolvedCropCode, site_code: resolvedSiteCode } });
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error fetching SPLot details';
                setIsMaterialValidating(false);
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const { mutate: checkMatidExists } = useCheckMatidExistMutation({
        mutation: {
            onSuccess: async (response: any) => {
                const data: any = response?.data;
                const responseItem: any = Array.isArray(data) ? (data[0] ?? {}) : (data ?? {});
                const exists = data && (Array.isArray(data) ? data.length > 0 : data);

                if (!exists) {
                    setIsMaterialValidating(false);
                    showSnackbar({ message: 'Material not found', severity: 'error' });
                    return;
                }

                // Threshold warning must be accepted before moving to the material form.
                if (responseItem?.warning === GenericConstant.THRESHOLDREACHED) {
                    // Drop the loader first, otherwise it covers the confirm dialog.
                    setIsMaterialValidating(false);
                    const userConfirmed: any = await confirm({
                        title: "Threshold Warning",
                        description: responseItem?.message,
                        confirmText: "Yes",
                        cancelText: "No",
                        severity: "warning",
                    });
                    if (!userConfirmed) {
                        confirmThresholdRef.current = false;
                        setPendingMatId('');
                        setScannedValue('');
                        return;
                    }
                    confirmThresholdRef.current = true;
                    setIsMaterialValidating(true);
                } else {
                    confirmThresholdRef.current = false;
                }

                setIsApplyingFieldRules(true);
                try {
                    //const cropsResponse = await refetchCrops();
                    //const cropsArray = (cropsResponse?.data?.data ?? cropsResponse?.data ?? []) as any[];

                    //const resolvedMandatory = getMaterialMandatoryMapByCrop(cropsArray, cropCodeRef.current);
                    // const resolvedDefaults = getMaterialDefaultValuesByCrop(cropsArray, cropCodeRef.current);

                    // setMandatoryFields(resolvedMandatory);
                    // Auto-populate fruit weight, fruit count, and bags from defaults
                    // setWeight(resolvedDefaults.weight !== undefined ? String(resolvedDefaults.weight) : '');
                    // setFruitCount(resolvedDefaults.fruit_count !== undefined ? String(resolvedDefaults.fruit_count) : '');
                    // setBags(resolvedDefaults.bag_number !== undefined ? String(resolvedDefaults.bag_number) : '');
                } catch {
                    // setMandatoryFields(ALL_MATERIAL_FIELDS_MANDATORY);
                    setWeight('');
                    setFruitCount('');
                    setBags('1');
                } finally {
                    setIsApplyingFieldRules(false);
                }

                const apiRemarkName = String(responseItem?.remark ?? '').trim().toLowerCase();
                const matchedRemark = remarks?.find((r: any) =>
                    String(r?.remark_name ?? '').trim().toLowerCase() === apiRemarkName
                );
                setCommentId(
                    responseItem?.remark_id !== undefined
                        ? String(responseItem.remark_id)
                        : matchedRemark?.remark_id !== undefined
                            ? String(matchedRemark.remark_id)
                            : ''
                );

                const apiPriorityValue = String(responseItem?.priority ?? '').trim().toLowerCase();
                const matchedPriority = priorityArr?.find((p: any) =>
                    String(p?.value ?? '').trim().toLowerCase() === apiPriorityValue
                );
                setPriority(
                    responseItem?.priority_id !== undefined
                        ? String(responseItem.priority_id)
                        : matchedPriority?.id !== undefined
                            ? String(matchedPriority.id)
                            : ''
                );

                setValidatedMaterialId(pendingMatId);
                setScannedValue(pendingMatId);
                setIsMaterialFormView(true);
                setIsMaterialValidating(false);
            },
            onError: (error: any) => {
                setIsMaterialValidating(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Material not found';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const { mutate: addMaterialToSplot } = useAddMaterial({
        mutation: {
            onSuccess: (data: any) => {
                setIsSaveLoading(false);
                if (data?.data?.error) {
                    showSnackbar({ message: data?.data?.error, severity: 'error' });
                    return;
                }

                showSnackbar({ message: 'Material Added Successfully', severity: 'success' });

                if (isAddMultipleMaterial) {
                    confirmThresholdRef.current = false;
                    setValidatedMaterialId('');
                    setScannedValue('');
                    setIsMaterialFormView(false);
                    setWeight('');
                    setFruitCount('');
                    setBags('1');
                    setCommentId('');
                    setPriority('');
                    setSiteCode('');
                    setCropCode('');
                    cropCodeRef.current = '';
                    // setMandatoryFields(ALL_MATERIAL_FIELDS_MANDATORY);
                } else {
                    onSaveToHome();
                }
            },
            onError: (error: any) => {
                setIsSaveLoading(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error in adding Material';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const handleAddMaterial = () => {
        const trimmed = scannedValue.trim();
        if (!trimmed) {
            return;
        }

        setPendingMatId(trimmed);
        setIsMaterialValidating(true);
        // Fetch SPLot workflow details to get site_code and crop_code
        getSPLotWorkflowDetails({
            data: { splot_ids: [spLotId] }
        });
    };

    const isSaveDisabled =
        // (mandatoryFields.weight && !weight.trim()) ||
        // (mandatoryFields.fruit_count && !fruitCount.trim()) ||
        // (mandatoryFields.bag_number && !bags.trim()) ||
        isSaveLoading ||
        isApplyingFieldRules;
    const isActionInProgress = isMaterialValidating || isSaveLoading || isApplyingFieldRules;

    const handleSave = () => {
        if (!validatedMaterialId) {
            showSnackbar({ message: 'Material ID is required', severity: 'warning' });
            return;
        }

        // Mandatory field validation for weight, fruit count, and bags
        // if (mandatoryFields.weight && !weight.trim()) {
        //     showSnackbar({ message: 'Weight (g) is required', severity: 'warning' });
        //     return;
        // }
        // if (weight.trim() && Number(weight) < 0) {
        //     showSnackbar({ message: 'Enter valid Weight (g)', severity: 'warning' });
        //     return;
        // }
        //
        // if (mandatoryFields.fruit_count && !fruitCount.trim()) {
        //     showSnackbar({ message: 'Fruit Count is required', severity: 'warning' });
        //     return;
        // }
        // if (fruitCount.trim() && Number(fruitCount) < 0) {
        //     showSnackbar({ message: 'Enter valid Fruit Count', severity: 'warning' });
        //     return;
        // }
        //
        // if (mandatoryFields.bag_number && !bags.trim()) {
        //     showSnackbar({ message: 'Bags count is required', severity: 'warning' });
        //     return;
        // }
        // if (bags.trim() && Number(bags) < 0) {
        //     showSnackbar({ message: 'Enter valid Bags', severity: 'warning' });
        //     return;
        // }

        const payload: any = {
            user_id: globalState?.user_id_db,
            splot_id: spLotId,
            mat_id: validatedMaterialId.trim(),
            weight: Number(weight),
            fruit_count: Number(fruitCount),
            bag_number: bags ? Number(bags) : 0,
            remark_id: commentId ? Number(commentId) : 0,
            priority: priority ?? '',
            crop_code: cropCode,
            site_code: siteCode,
            confirm_threshold: confirmThresholdRef.current,
        };

        setIsSaveLoading(true);
        addMaterialToSplot({ data: payload });
    };

    const handleHeaderBack = () => {
        if (isMaterialFormView) {
            confirmThresholdRef.current = false;
            setIsMaterialFormView(false);
            setSiteCode('');
            setCropCode('');
            cropCodeRef.current = '';
            // setMandatoryFields(ALL_MATERIAL_FIELDS_MANDATORY);
            return;
        }
        onBack();
    };

    return (
        <>
        <FullScreenActionLoader open={isActionInProgress} />
        <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ minHeight: 'calc(100vh - 140px)' }}
        >
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <Divider sx={{ width: '100%' }} />
                <Box display="flex" width="100%" alignItems="center" justifyContent="flex-start">
                    <ArrowBackIosIcon
                        sx={{ verticalAlign: 'middle', cursor: 'pointer', fontSize: 28 }}
                        onClick={handleHeaderBack}
                    />
                    <Typography component="div" sx={{ py: 1.25, px: 1.5 }} className={classes.labelNoto20Bold500_hh}>
                        Add New Materials
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: 'grey.400', borderBottomWidth: '1px', width: '100%' }} />
            </Box>

            <Box sx={{ width: '100%', pt: '16px', px: 2, pb: 0 }}>
                <Typography component="div" className={classes.labelNoto16Bold400_hh}>
                    SP Lot : <Box component="span" sx={{ fontWeight: 700, ...(spLotId ? {} : { fontSize: 16 }) }}>{spLotId || DASH_PLACEHOLDER}</Box>
                </Typography>
            </Box>

            <Box sx={{ width: '100%', pt: '16px', px: 2, pb: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                {!isMaterialFormView && (
                    <>
                        <Box sx={{ width: '100%', mt: '8px' }}>
                            <ScanButton />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ width: '100%', mt: '24px', alignItems: 'flex-end' }}>
                            <ScanInput
                                key="scan-input-material"
                                softFocusOnMount
                                submitOnScan={false}
                                scanPickupEnabled={false}
                                defaultValue={scannedValue}
                                onChange={(val: string) => setScannedValue(val)}
                                onScan={(val) => setScannedValue(val)}
                                onEnterPress={() => handleAddMaterial()}
                                manualEdit
                                error={false}
                                label={
                                    <>
                                        Material ID <Box component="span" sx={{ color: '#DC2626' }}>*</Box>
                                    </>
                                }
                                placeholder="Scan or Enter Material ID"
                                errorText=""
                            />
                            {scannedValue.trim() && (
                            <Button
                                variant="contained"
                                className={classes.blueButton_hh}
                                disabled={isMaterialValidating}
                                sx={{
                                    height: 40,
                                    width: 40,
                                    minWidth: 40,
                                    '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0, padding: 0 },
                                }}
                                startIcon={<ArrowForwardIcon sx={{ color: 'white', fontSize: 26 }} />}
                                onClick={handleAddMaterial}
                            />
                            )}
                        </Stack>
                    </>
                )}

                {isMaterialFormView && (
                    <>
                        <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                            <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5 }}>
                                Material ID <Box component="span" sx={{ color: '#DC2626' }}>*</Box>
                            </FormLabel>
                            <TextField
                                size="small"
                                inputRef={materialIdInputRef}
                                value={validatedMaterialId || scannedValue}
                                onChange={(e) => {
                                    setValidatedMaterialId(e.target.value);
                                    setScannedValue(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddMaterial();
                                    } else if (e.key === 'Tab') {
                                        handleAddMaterial();
                                    }
                                }}
                                placeholder="Scan or Enter Material ID"
                                sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
                            />
                        </FormControl>

                        <Box
                            sx={{
                                width: '100%',
                                mt: 2,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                gap: 1,
                                alignItems: 'start',
                                '@media (max-width:430px)': {
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                },
                                '@media (max-width:340px)': {
                                    gridTemplateColumns: 'minmax(0, 1fr)',
                                },
                            }}
                        >
                            <FormControl size="small" sx={{ minWidth: 0 }}>
                                <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5, whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                                    Fruit Weight (g)
                                </FormLabel>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={weight}
                                    onChange={(e) => { if (e.target.value === '' || Number(e.target.value) <= 1000) setWeight(e.target.value); }}
                                    onKeyDown={numericOnlyKeyDown}
                                    placeholder="0.0"
                                    slotProps={{ htmlInput: { min: 0, max: 1000 } }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
                                />
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 0 }}>
                                <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5, whiteSpace: 'normal', lineHeight: 1.2 }}>
                                    Fruit Count
                                </FormLabel>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={fruitCount}
                                    onChange={(e) => { if (e.target.value === '' || Number(e.target.value) <= 1000) setFruitCount(e.target.value); }}
                                    placeholder="0"
                                    onKeyDown={numericOnlyKeyDown}
                                    slotProps={{ htmlInput: { min: 0, max: 1000 } }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
                                />
                            </FormControl>

                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: 0,
                                    '@media (max-width:430px)': {
                                        gridColumn: '1 / -1',
                                    },
                                    '@media (max-width:340px)': {
                                        gridColumn: 'auto',
                                    },
                                }}
                            >
                                <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5, whiteSpace: 'normal', lineHeight: 1.2 }}>
                                    Bags
                                </FormLabel>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={bags}
                                    onChange={(e) => { if (e.target.value === '' || Number(e.target.value) <= 1000) setBags(e.target.value); }}
                                    placeholder="0"
                                    onKeyDown={numericOnlyKeyDown}
                                    slotProps={{ htmlInput: { min: 0, max: 1000 } }}
                                    sx={{ '& .MuiOutlinedInput-root': { height: 40 } }}
                                />
                            </FormControl>
                        </Box>

                        <FormControl size="small" fullWidth sx={{ mt: '10px' }}>
                            <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5 }}>
                                Remarks
                            </FormLabel>
                            <Select
                                IconComponent={KeyboardArrowDownOutlinedIcon}
                                value={commentId}
                                onChange={(e) => setCommentId(String(e.target.value))}
                                displayEmpty
                                sx={{ height: 40 }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <Typography component="span" sx={{ color: '#9CA3AF' }}>Add Remark</Typography>;
                                    }
                                    return remarks?.find((r: any) => String(r?.remark_id) === String(selected))?.remark_name ?? '';
                                }}
                            >
                                {remarks?.map((remark: any) => (
                                    <MenuItem key={`remark-${remark?.remark_id}`} value={String(remark?.remark_id)}>
                                        {remark?.remark_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" fullWidth sx={{ mt: 2 }}>
                            <FormLabel className={classes.labelNoto14Bold400_hh} sx={{ color: '#4B5563', mb: 0.5 }}>
                                Priority
                            </FormLabel>
                            <Select
                                IconComponent={KeyboardArrowDownOutlinedIcon}
                                value={priority}
                                onChange={(e) => setPriority(String(e.target.value))}
                                displayEmpty
                                sx={{ height: 40 }}
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return <Typography component="span" sx={{ color: '#9CA3AF' }}>Choose Priority</Typography>;
                                    }
                                    return priorityArr?.find((p: any) => String(p?.id) === String(selected))?.value ?? '';
                                }}
                            >
                                {priorityArr?.map((p: any) => (
                                    <MenuItem key={`priority-${p?.id}`} value={String(p?.id)}>
                                        {p?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            sx={{ mt: 1 }}
                            className={classes.labelNoto16Bold400_hh}
                            control={
                                <Checkbox
                                    checked={isAddMultipleMaterial}
                                    onChange={(e) => {
                                        setIsAddMultipleMaterial(e.target.checked);
                                        setTimeout(() => {
                                            materialIdInputRef.current?.focus();
                                        }, 0);
                                    }}
                                    sx={{
                                        '&.Mui-checked': {
                                            color: '#000000',
                                        },
                                    }}
                                />
                            }
                            label="Add Multiple Materials"
                        />

                        <Button
                            variant="contained"
                            disableRipple
                            // className={classes.greenButton}
                            className={classes.labelNoto16Bold400_hh}
                            loading={isSaveLoading}
                            loadingPosition="start"
                            disabled={isSaveDisabled}
                            sx={{
                                height: 48,
                                padding: '12px 127px',
                                gap: '6px',
                                opacity: 1,
                                borderRadius: '12px',
                                backgroundColor: '#008F35',
                                boxShadow: '0px 4px 6px -4px #0000001A',
                                color: isSaveDisabled ? '#9CA3AF' : '#FFFFFF',
                                textTransform: 'none',
                                width: '100%',
                                mt: 'auto',
                            }}
                            onClick={handleSave}
                        >
                            Save
                        </Button>
                    </>
                )}
            </Box>
        </Box>
        </>
    );
}
